import clsx from "clsx";
import React, { useEffect } from "react";

import {
  DELAY_MINIMUM_CLOSE_TIME,
  getBase64FileList,
} from "../../../libs/common";

import useSimplePopup from "../../../hooks/useSimplePopup";

import ImagePreview from "../ImagePreview";
import ImageUploadPopup from "../../Popup/ImageUpload";

type Props = {
  maxImages: number;
  images: (File | string)[];
  onChange: (images: (File | string)[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  thumbnailPosition?: "top" | "right";
  title?: string;
  type?: "profile" | "board" | "workBefore" | "workAfter" | "center";
};

export default function CommonImageInput({
  maxImages,
  images,
  onChange,
  setIsLoading,
  title,
  type = "profile",
  thumbnailPosition = "right",
}: Props) {
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [popup, setPopup] = React.useState("");
  const [delayedPopup, setDelayedPopup] = React.useState("");

  const handleImageUpload = async (base64StringList?: string[]) => {
    // base64 URI 문자열을 Blob 객체로 변환
    if (!base64StringList || base64StringList.length <= 0) {
      setIsLoading(false);
      return;
    }

    const fileList = await getBase64FileList(base64StringList, "profile");

    if (fileList.length + images.length <= maxImages) {
      onChange([...images, ...fileList]);
      setPopup("");
    } else {
      open(`이미지는 최대 ${maxImages}장까지\n첨부할 수 있어요`);
    }
    setIsLoading(false);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  };

  // 팝업 닫힘 지연 처리
  useEffect(() => {
    if (popup) {
      setDelayedPopup(popup);
    } else {
      setTimeout(() => {
        setDelayedPopup(popup);
      }, DELAY_MINIMUM_CLOSE_TIME);
    }
  }, [popup]);

  return (
    <div
      className={clsx([
        "gap-2.5",
        {
          flex: thumbnailPosition === "right",
          "flex flex-col": thumbnailPosition === "top",
        },
      ])}
    >
      {thumbnailPosition === "top" && images && images.length > 0 && (
        <div>
          <div className="grid grid-cols-3 gap-2.5">
            {images?.map((image, index, imageList) => (
              <ImagePreview
                key={index}
                image={image}
                imageList={imageList}
                onRemove={handleRemoveImage}
                index={index}
                className="max-h-full max-w-full"
              />
            ))}
          </div>
        </div>
      )}
      <label
        className="border border-brand-neutral-80 w-full flex items-center justify-center cursor-pointer font-medium text-base gap-2 rounded-[3px] h-12"
        onClick={() => {
          if (images.length === maxImages) {
            open(`이미지는 최대 ${maxImages}장까지\n첨부할 수 있어요`);
          } else {
            setPopup("imageUpload-" + type);
          }
        }}
      >
        <CameraIcon /> 사진 추가
      </label>
      {thumbnailPosition === "right" && images && images.length > 0 && (
        <div className="flex flex-wrap">
          {images?.map((image, index, imageList) => (
            <ImagePreview
              key={index}
              image={image}
              imageList={imageList}
              onRemove={handleRemoveImage}
              index={index}
            />
          ))}
        </div>
      )}

      {delayedPopup === `imageUpload-${type}` && (
        <ImageUploadPopup
          type={type}
          popup={popup}
          onClose={() => setPopup("")}
          onChangeImage={handleImageUpload}
          maxCount={maxImages - images.length}
          onCall={() => {
            setIsLoading(true);
          }}
          title={title}
        />
      )}

      {simplePopupComponent}
    </div>
  );
}

export const GalleryPictureIcon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="79"
      height="79"
      rx="2.5"
      fill="white"
      stroke="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M37.5465 35.455C37.5465 37.6039 35.8063 39.3441 33.6574 39.3441C31.5106 39.3441 29.7683 37.6039 29.7683 35.455C29.7683 33.3061 31.5106 31.5659 33.6574 31.5659C35.8042 31.568 37.5444 33.3082 37.5465 35.455Z"
      stroke="url(#paint0_linear_799_14129)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23.2292 56.5131L30.8148 46.4281H31.0298L36.3769 51.2269H36.7642L44.93 39.3441H45.2277L57.063 56.0389"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M57.5 57.4998L57.5 22.4998L22.5 22.4998L22.5 57.4998L57.5 57.4998Z"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M57.5 27.5L57.5 22.5L52.5 22.5"
      stroke="url(#paint1_linear_799_14129)"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <defs>
      <linearGradient
        id="paint0_linear_799_14129"
        x1="37.5465"
        y1="31.5659"
        x2="29.2654"
        y2="33.6973"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_799_14129"
        x1="52.5"
        y1="22.5"
        x2="53.8701"
        y2="27.8233"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const TakePictureIcon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="79"
      height="79"
      rx="2.5"
      fill="white"
      stroke="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M46.6434 22.5L50.4284 28.4121H60.2142L60.2142 57.5H19.7859L19.7859 28.4121H29.5717L33.3545 22.5H46.6434Z"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M52.9527 36.7985H52.9724"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M46.9457 42.6529C46.9457 38.8155 43.836 35.7058 39.9986 35.7058C36.1612 35.7058 33.0515 38.8155 33.0515 42.6529C33.0515 46.4903 36.1612 49.6 39.9986 49.6C43.836 49.6 46.9457 46.4903 46.9457 42.6529Z"
      stroke="url(#paint0_linear_799_14128)"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <defs>
      <linearGradient
        id="paint0_linear_799_14128"
        x1="46.9457"
        y1="35.7058"
        x2="32.1531"
        y2="39.513"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const CameraIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.04 3.99219L16.772 6.69755H21.25L21.25 20.0082H2.75001L2.75 6.69755H7.228L8.95901 3.99219H15.04Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M17.9271 10.5352H17.9361"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.1783 13.2142C15.1783 11.4582 13.7553 10.0352 11.9993 10.0352C10.2433 10.0352 8.82031 11.4582 8.82031 13.2142C8.82031 14.9702 10.2433 16.3932 11.9993 16.3932C13.7553 16.3932 15.1783 14.9702 15.1783 13.2142Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);
